
import { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { plans } from '../data/plans';

interface SearchBarProps {
  onSearch: (query: string) => void;
  onClose: () => void;
  className?: string;
  placeholder?: string;
}

export const SearchBar = ({ 
  onSearch, 
  onClose,
  className = '',
  placeholder = 'Søg efter udbyder, abonnement eller datamængde...'
}: SearchBarProps) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [placeholderText, setPlaceholderText] = useState('');
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  // Search examples for typing animation
  const searchExamples = ['Fri tale', 'Fri data', 'CBB', 'Oister', 'Viaplay', 'TV2 Play'];
  
  // Typing animation for placeholder examples
  useEffect(() => {
    const typingDelay = 100; // Delay between typing each character
    const deletingDelay = 50; // Faster when deleting
    const pauseDelay = 1500; // Pause when complete example is shown
    
    if (isPaused) {
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseDelay);
      return () => clearTimeout(pauseTimer);
    }
    
    const example = searchExamples[currentExampleIndex];
    
    if (!isDeleting && placeholderText !== example) {
      const timer = setTimeout(() => {
        setPlaceholderText(example.substring(0, placeholderText.length + 1));
      }, typingDelay);
      return () => clearTimeout(timer);
    }
    
    if (!isDeleting && placeholderText === example) {
      setIsPaused(true);
      return;
    }
    
    if (isDeleting && placeholderText) {
      const timer = setTimeout(() => {
        setPlaceholderText(example.substring(0, placeholderText.length - 1));
      }, deletingDelay);
      return () => clearTimeout(timer);
    }
    
    if (isDeleting && !placeholderText) {
      setIsDeleting(false);
      setCurrentExampleIndex((prevIndex) => (prevIndex + 1) % searchExamples.length);
    }
  }, [placeholderText, currentExampleIndex, isDeleting, isPaused, searchExamples]);
  
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    
    const providerSuggestions = [...new Set(plans
      .map(plan => plan.provider)
      .filter(provider => provider.toLowerCase().includes(lowerQuery))
    )];
    
    const planNameSuggestions = [...new Set(plans
      .map(plan => plan.name)
      .filter(name => name.toLowerCase().includes(lowerQuery))
    )];
    
    const dataAllowanceSuggestions = [...new Set(plans
      .map(plan => plan.data_allowance)
      .filter(data => data.toLowerCase().includes(lowerQuery))
    )];
    
    setSuggestions([
      ...providerSuggestions,
      ...planNameSuggestions,
      ...dataAllowanceSuggestions
    ].slice(0, 5));
  }, [query]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (query.trim()) {
      // Handle special case for "fri data" or similar searches
      const lowerQuery = query.toLowerCase();
      if (lowerQuery.includes("fri") && lowerQuery.includes("data")) {
        onSearch("Ubegrænset");
      } else if (lowerQuery === "fri") {
        onSearch("Ubegrænset");
      } else {
        onSearch(query);
      }
      setIsFocused(false);
    }
  };
  
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    onSearch(suggestion);
    setIsFocused(false);
  };
  
  const clearSearch = () => {
    setQuery('');
    onSearch('');
    inputRef.current?.focus();
  };
  
  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative flex">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="h-5 w-5 text-muted-foreground" />
          </div>
          <input
            ref={inputRef}
            type="text"
            className="w-full py-3 px-10 bg-white/70 backdrop-blur-md border border-input rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 transition-all duration-200"
            placeholder={isFocused ? placeholder : `${placeholder} (f.eks. ${placeholderText})`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            aria-label="Søgefelt"
          />
          {query && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={clearSearch}
              aria-label="Ryd søgning"
            >
              <X className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
            </button>
          )}
        </div>
      </form>
      
      {isFocused && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-10 w-full mt-2 bg-white rounded-lg shadow-lg border border-border py-2 animate-in animate-fade-in"
          role="listbox"
          aria-label="Søgeforslag"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              className="w-full text-left px-4 py-2 hover:bg-secondary transition-colors"
              onClick={() => handleSuggestionClick(suggestion)}
              role="option"
              aria-selected={false}
              aria-label={`Søg efter ${suggestion}`}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
